/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { MaterialsService } from '../materials.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject, take, tap } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { MaterialsDaoService } from '../../dao/materials-dao.service';
import { Material, MaterialDTO } from 'src/app/_model/material';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { TranslateService } from '@ngx-translate/core';
import { LBSUtils } from 'src/app/_util/lbs-utils';

/**
 * Loads Materials
 */
@Injectable()
export class MaterialsLogicService implements MaterialsService {
  public materials$ = new BehaviorSubject<Material[]>([]);
  public materialsOnTheMove$ = new BehaviorSubject<number[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private remoteActionService: RemoteActionService,
    private materialsDaoService: MaterialsDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  // Public Functions
  public getMaterialById(id: IMSId): Material {
    if (id === undefined) {
      return undefined;
    }

    let mat = this.materials$.getValue().find((mat) => {
      if (mat.id.imsId === id.imsId && mat.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
    return mat;
  }

  public getMaterialByExtid(extid: string): Material {
    if (extid === undefined) {
      return undefined;
    }

    let mat = this.materials$.getValue().find((mat) => {
      return mat.extid === extid
    });
    return mat;
  }

  public getUnknownMaterialPlaceholder(): Material {
    let mat = this.materials$
      .getValue()
      .find((mat) => mat.isPlaceholderMaterial);
    return mat;
  }

  // Resetable
  public async reset() {
    await this.materialsDaoService.reset();
  }

  public async clear() {
    await this.materialsDaoService.clear();
    this.materials$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteMaterials(apiLevel, syncKey);
  }
  public async postsync(): Promise<void> {}
  public async presync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalMaterials();
      
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalMaterials() {
    try {
      this.logger.log(this.constructor.name, '- Load local Materials');
      let materials: Material[] = await this.materialsDaoService.getMaterials();

      this.logger.log(
        this.constructor.name,
        '- Loaded',
        materials?.length,
        'local Materials',
      );
      this.materials$.next(materials);

      let matsOnTheMove =
        await this.materialsDaoService.getMaterialsOnTheMove();
      this.materialsOnTheMove$.next(matsOnTheMove);
      this.logger.log(
        this.constructor.name,
        '- loaded matsOnTheMove from storage',
        matsOnTheMove?.length,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Materials: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteMaterials(apiLevel: APILevel, syncKey: string ) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote Materials from IMS',
      );

      try {
        let newMaterials: Material[] = await this.loadRemoteMaterialBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading' + newMaterials.length + ' new materials',
        );

        // this.materials$.next(newMaterials)
        await this.loadLocalMaterials();
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Materials: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  private async loadRemoteMaterialBatch(
    syncKey: string,
    materials: Material[],
    nextPart?: String,
  ): Promise<Material[]> {
    let materialsData;
    const nextSyncTstamp = Date.now();

    if (nextPart) {
      materialsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        }),
      );

      this.logger.log(
        this.constructor.name,
        'received materials with nextpart request with',
        materialsData?.length,
        'materials',
      );
    } else {
      let remoteActionData = {
        endpoint: 'v2/syncmaterial',
        cachable: false,
        fifo: false,
        authenticationRequired: true,
        data: {
          lastSyncTstamp: await this.materialsDaoService.getLastSyncTstamp(),
          synckey: syncKey
        },
      };

      this.logger.log(
        this.constructor.name,
        ' generate RemoteAction with lastsyncTstamo',
        remoteActionData.data?.lastSyncTstamp,
      );

      let materialsDataContainer =
        await this.remoteActionService.sendRemoteAction(
          new RemoteAction(remoteActionData),
        );

      this.logger.log(
        this.constructor.name,
        ' received materialsDataContainer with',
        materialsDataContainer?.materials?.length,
        'materials',
      );

      materialsData = materialsDataContainer?.materials;
      let idsToDelete = materialsDataContainer?.deletedIds;
      this.logger.log(this.constructor.name, ' deletedIds', idsToDelete);
      if (idsToDelete) {
        await this.materialsDaoService.deleteMaterials(idsToDelete);
      }

      let idsOnTheMove = materialsDataContainer?.materialsonthemove;
      if (idsOnTheMove) {
        this.logger.log(
          this.constructor.name,
          '- found materials on the move',
          idsOnTheMove.length,
        );
        await this.materialsDaoService.saveMaterialsOnTheMove(idsOnTheMove);
      } else {
        this.logger.log(
          this.constructor.name,
          '- no materials on the move found, reset storage',
        );
        await this.materialsDaoService.removeMaterialsOnTheMove();
      }

      this.materialsOnTheMove$.next(idsOnTheMove);
      this.logger.log(
        this.constructor.name,
        '- loaded matsOnTheMove to subject',
        idsOnTheMove?.length,
      );

      if (materialsDataContainer?.nlsobject) {
        await this.materialsDaoService.saveMaterialsTranslateObject(
          materialsDataContainer.nlsobject,
        );
        LBSUtils.mergeTranslations(this.translate, materialsDataContainer.nlsobject);
      }
    }

    let nextId: string;
    let nextMaterials: Material[] = [];
    for (const materialData of materialsData) {
      if (materialData['#nextid']) {
        nextId = materialData['#nextid'];
      } else {
        nextMaterials.push(Material.fromDto(materialData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch Materials: ',
      nextMaterials.length,
      'Total',
    );

    await this.materialsDaoService.saveMaterials(nextMaterials);

    let returnMaterials = materials.concat(nextMaterials);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more materials with nextid',
        nextId,
      );
      return await this.loadRemoteMaterialBatch(syncKey, returnMaterials, nextId);
    }

    this.logger.log(
      this.constructor.name,
      'finished syncing all batches save tstamp',
      nextSyncTstamp,
    );
    await this.materialsDaoService.setLastSyncTstamp(nextSyncTstamp);

    return returnMaterials;
  }

  async initializeCompleted(): Promise<void> {
    const translateObject = await this.materialsDaoService.getMaterialsTranslateObject();
      if (translateObject) {
        LBSUtils.mergeTranslations(this.translate, translateObject)
      }
  }
}
